import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Undo2, Loader2 } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: () => void;
  onUndo?: () => void;
}

const Toast: React.FC<ToastProps> = ({ 
  message, 
  variant = 'info', 
  duration = 5000,
  onClose,
  onUndo
}) => {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);
  const [remainingTime, setRemainingTime] = useState(duration);
  const [isUndoing, setIsUndoing] = useState(false);

  // Standard SaaS: Auto-dismiss with pause on hover
  // For toasts with Undo: auto-dismiss after duration (5-7s) to confirm deletion
  // User can click Undo before it disappears, or let it disappear to confirm
  useEffect(() => {
    // Don't auto-dismiss while undoing
    if (isUndoing) return;
    
    if (isHovered) {
      // Pause timer when hovered (standard SaaS practice)
      return;
    }

    if (remainingTime <= 0) {
      // When toast auto-dismisses, if there's an undo callback, it means deletion is confirmed
      // Don't call onUndo - the deletion is final
      onClose();
      return;
    }

    const timer = setTimeout(() => {
      setRemainingTime(prev => Math.max(0, prev - 100));
    }, 100);

    return () => clearTimeout(timer);
  }, [remainingTime, isHovered, isUndoing, onClose]);

  // Reset remaining time when duration changes
  useEffect(() => {
    setRemainingTime(duration);
  }, [duration]);

  const variantStyles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-primary-50 border-primary-200 text-primary-800',
  };

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-600" />,
    error: <AlertCircle className="w-5 h-5 text-red-600" />,
    warning: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
    info: <Info className="w-5 h-5 text-primary-600" />,
  };

  return (
    <div 
      className={`min-w-[300px] max-w-md ${variantStyles[variant]} border-2 rounded-lg shadow-lg p-4 flex items-start space-x-3 transition-all duration-300 ease-in-out animate-in slide-in-from-right-5 fade-in`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex-shrink-0 mt-0.5">
        {icons[variant]}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>
      <div className="flex items-center space-x-2">
        {onUndo && (
          <button
            onClick={async () => {
              if (isUndoing) return;
              setIsUndoing(true);
              try {
                await onUndo();
              } finally {
                onClose();
              }
            }}
            disabled={isUndoing}
            className={`flex-shrink-0 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-opacity-90 transition-all flex items-center disabled:opacity-70 disabled:cursor-not-allowed ${
              variant === 'success'
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : variant === 'error'
                  ? 'bg-red-100 text-red-800 hover:bg-red-200'
                  : variant === 'warning'
                    ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                    : 'bg-primary-100 text-primary-800 hover:bg-primary-200'
            }`}
          >
            {isUndoing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                {t('common.undoing') || 'Annulation...'}
              </>
            ) : (
              <>
                <Undo2 className="w-4 h-4 mr-1.5" />
                {t('common.undo')}
              </>
            )}
          </button>
        )}
        {/* Standard SaaS: No close button (X) when undo is present - only Undo CTA */}
        {!onUndo && (
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Toast;

